import { Box, Typography, IconButton, Card, CardActionArea } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferSearchResult } from '@/features/search/types/search.types';
import { useSnackbar } from '@/shared/hooks/useSnackbar';
import { StatusBadge } from '@/shared/components';
import { PLACEHOLDER_IMAGES } from '@/shared/constants/placeholders';
import offerDetailService from '@/features/offer-detail/services/offerDetailService';
import { useSearchContext } from '@/shared/contexts/SearchContext';

interface FavoriteCardProps {
  readonly offer: OfferSearchResult;
  readonly onFavoriteUpdate?: (offerId: string, isFavorite: boolean) => void;
}

export function FavoriteCard({ offer, onFavoriteUpdate }: Readonly<FavoriteCardProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { updateFavoriteInResults } = useSearchContext();
  const [isUpdating, setIsUpdating] = useState(false);

  const firstProduct = offer.products[0];
  const firstPhoto = firstProduct?.photos[0];
  const productName = firstProduct?.material?.subcategory || firstProduct?.name || '';

  // Format quantity
  const quantity = firstProduct?.quantity || 0;
  const unitLabel = firstProduct?.unit?.labelFr || '';
  const quantityText = `${quantity} ${unitLabel}${quantity > 1 ? 's' : ''}`;

  // Format availability
  const getAvailabilityText = () => {
    // If we have specific dates, format them
    if (offer.startDate && offer.endDate) {
      const start = new Date(offer.startDate);
      const end = new Date(offer.endDate);
      const formatDate = (date: Date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      return `Du ${formatDate(start)} au ${formatDate(end)}`;
    }
    return t('favorites.availableNow');
  };

  const handleCardClick = () => {
    navigate(`/offers/${offer.id}`, {
      state: {
        from: 'favorites',
        offerData: offer,
      },
    });
  };

  const handleFavoriteClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    try {
      setIsUpdating(true);
      await offerDetailService.toggleFavorite(offer.id, true);
      onFavoriteUpdate?.(offer.id, false);
      // Keep search context in sync so the favorite icon is correct when
      // navigating back to the Search page without re-fetching.
      updateFavoriteInResults(offer.id, false);
      showSnackbar(t('favorites.removedSuccess'), { variant: 'success' });
    } catch (error: unknown) {
      console.error('Failed to remove favorite', error);
      showSnackbar(t('favorites.removedError'), { variant: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        position: 'relative',
      }}
    >
      <CardActionArea onClick={handleCardClick}>
        <Box sx={{ display: 'flex', p: 2, gap: 2 }}>
          {/* Image */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '8px',
              overflow: 'hidden',
              flexShrink: 0,
              bgcolor: 'grey.100',
            }}
          >
            <img
              src={firstPhoto?.thumbnailUrl || firstPhoto?.url || PLACEHOLDER_IMAGES.product}
              alt={offer.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {offer.title || productName}
            </Typography>

            {/* Product Name */}
            {productName && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {productName}
              </Typography>
            )}

            {/* Location */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {offer.address}
              </Typography>
            </Box>

            {/* Quantity */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Quantité: {quantityText}
            </Typography>

            {/* Status Badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <StatusBadge status={firstProduct?.status || 'USED'} size="small" />
            </Box>

            {/* Availability */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {getAvailabilityText()}
              </Typography>
            </Box>
          </Box>

          {/* Favorite Button */}
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <IconButton
              onClick={handleFavoriteClick}
              disabled={isUpdating}
              sx={{
                bgcolor: 'white',
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
              data-testid={`favorite-toggle-${offer.id}`}
            >
              <FavoriteIcon sx={{ color: '#FFD700', fontSize: 28 }} />
            </IconButton>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}
